/* global gigya */
/* eslint-disable no-useless-escape */
import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Button from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useLocation} from '@s-ui/react-router'
import LoginGigya from '@adv-mt/mt-login-gigya'
import IconAdCar from '@adv-mt/mt-svg-icons/lib/Car.js'

import SecurityValidationImage from './SecurityValidationImage.js'

const URL_PARAMETER_MONOLITH = 'p'
const URL_PARAMETER_RESET_PASSWORD = 'pwrt'
const DEFAULT_TARGET_PAGE = 'default'
const PTA_TARGET_PAGE = 'pta'
const TARGET_PAGES = [DEFAULT_TARGET_PAGE, PTA_TARGET_PAGE]

const LoginContainer = ({targetPage = DEFAULT_TARGET_PAGE}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const loginMotivation = config.get('login').LOGIN_MOTIVATION
  const registerScreen = config.get('LOGIN_SCREENSET_ID_MAPPING').register
  const loginScreen = config.get('LOGIN_SCREENSET_ID_MAPPING').login
  const [hideFeatures, setHideFeatures] = useState(false)
  const {query} = useLocation()

  useMount(() => {
    // Little hack for the experiment abtest8_web_newvehicle_call_logged_in
    const isPhoneCallExperiment = query?.targetPage?.includes('phoneCall=true')
    if (isPhoneCallExperiment) window.scrollTo(0, 0)
  })

  const handleShowRegisterScreen = () => _showScreenset(registerScreen)
  const handleShowLoginScreen = () => _showScreenset(loginScreen)

  const _hideFeatures = () => setHideFeatures(true)

  const _showScreenset = screen => {
    _hideFeatures()
    _gigyaSwitchScreenEvent(screen)
  }

  const _getParameterByName = (name, url) => {
    if (!url) url = window.location.href
    name = name.replace(/[\[\]]/g, '\\$&')
    const regex = new RegExp(`[?&]${name}=([^&#]*)`)
    const results = regex.exec(url)

    if (!results) return null
    if (!results[1]) return ''
    return decodeURIComponent(results[1].replace(/\+/g, ' '))
  }

  const _gigyaSwitchScreenEvent = screen => {
    gigya &&
      gigya.accounts.switchScreen({
        screenSet: config.get('GIGYA_CONFIG').screenSet,
        screen,
        containerID: config.get('GIGYA_CONFIG').containerID
      })
  }

  const _hasScreensetParameters = () =>
    _getParameterByName(URL_PARAMETER_MONOLITH) || _getParameterByName(URL_PARAMETER_RESET_PASSWORD)

  const _printLoginMotivationImage = targetPage => {
    const {
      ENCODED_ICON: encodedIcon,
      SCRIPT_INDEXES: [openingScriptIndex, closingScriptIndex]
    } = config.get('SECURITY_VALIDATION')

    return targetPage === PTA_TARGET_PAGE ? (
      <IconAdCar svgClass="mt-LoginContainer-innerIcon" />
    ) : (
      <SecurityValidationImage
        encodedIcon={encodedIcon}
        openingScriptIndex={openingScriptIndex}
        closingScriptIndex={closingScriptIndex}
        altText={i18n.t('LOGIN_CONTAINER_LOGIN_TEXT')}
      />
    )
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const _printMotivationInfo = () => {
    const loginMotivationByPage = loginMotivation[targetPage] || loginMotivation[DEFAULT_TARGET_PAGE]
    let {text: loginMotivationText} = loginMotivationByPage
    // Experiment abtest8_web_newvehicle_call_logged_in
    const isPhoneCallExperiment = query?.targetPage?.includes('phoneCall=true')
    loginMotivationText = isPhoneCallExperiment
      ? 'LOGIN_CONTAINER_COCHES_MOTIVATION_DETAIL_PHONECALL_TEXT'
      : loginMotivationText
    return (
      <div>
        {_printLoginMotivationImage(targetPage)}
        <h1
          className="mt-LoginContainer-feature"
          dangerouslySetInnerHTML={{
            __html: i18n.t(loginMotivationText, {
              className: 'mt-LoginContainer-bold'
            })
          }}
        />
      </div>
    )
  }

  useMount(() => {
    if (isMobile && _hasScreensetParameters()) {
      _hideFeatures()
    }
  })

  const containerClass = cx('mt-LoginContainer', {
    'is-formToggled': hideFeatures
  })

  return (
    <div className={containerClass}>
      <div className="mt-LoginContainer-inner">
        <div className="mt-LoginContainer-innerFeatures">
          {_printMotivationInfo()}
          <div className="mt-LoginContainer-buttonsWrapper">
            <div className="mt-LoginContainer-buttons">
              <Button
                onClick={handleShowRegisterScreen}
                color="accent"
                shape="circular"
                size="large"
                fullWidth
                isFitted
              >
                {i18n.t('LOGIN_CONTAINER_REGISTER_TEXT')}
              </Button>
              <Button
                onClick={handleShowLoginScreen}
                design="outline"
                negative
                color="primary"
                shape="circular"
                size="large"
                fullWidth
                isFitted
              >
                {i18n.t('LOGIN_CONTAINER_LOGIN_TEXT')}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-LoginContainer-innerForm">
          <LoginGigya />
        </div>
      </div>
    </div>
  )
}

LoginContainer.displayName = 'LoginContainer'

LoginContainer.propTypes = {
  targetPage: PropTypes.oneOf(TARGET_PAGES)
}

export default LoginContainer
